import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Check, Phone, Bot, Zap, Building2, ArrowRight, Shield, Star } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function PlanSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/onboarding/plans`);
      setPlans(res.data.plans.filter(p => p.id !== "free_trial"));
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
    setLoading(false);
  };

  const handleSelectPlan = async (planId) => {
    if (planId === "enterprise") {
      // Redirect to contact sales
      window.location.href = "mailto:info@gskproductions.ca?subject=Enterprise%20Plan%20Inquiry";
      return;
    }

    setSelectedPlan(planId);
    
    try {
      const res = await axios.post(`${API}/api/v1/onboarding/start`, {
        plan_id: planId
      });
      
      // Store session token and navigate to qualification
      localStorage.setItem("onboarding_session", res.data.session_token);
      localStorage.setItem("onboarding_plan", planId);
      navigate("/onboarding/qualify");
    } catch (error) {
      console.error("Failed to start onboarding:", error);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case "ai_receptionist": return <Phone className="w-6 h-6" />;
      case "ai_sales_agent": return <Bot className="w-6 h-6" />;
      case "smb_power_pack": return <Zap className="w-6 h-6" />;
      case "enterprise": return <Building2 className="w-6 h-6" />;
      default: return <Phone className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case "ai_receptionist": return "from-blue-500 to-blue-600";
      case "ai_sales_agent": return "from-purple-500 to-purple-600";
      case "smb_power_pack": return "from-amber-500 to-amber-600";
      case "enterprise": return "from-slate-600 to-slate-700";
      default: return "from-blue-500 to-blue-600";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-8 h-8 text-blue-500" />
            <span className="text-xl font-bold">Speakr</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <span className="flex items-center gap-1">
              <Shield className="w-4 h-4" /> SOC2 Compliant
            </span>
            <span>|</span>
            <span>PIPEDA Ready</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-6 py-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Choose Your AI Voice Agent
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
          Enterprise-grade AI voice agents for Canadian businesses. 
          Start your 14-day free trial today.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-2 rounded-lg transition ${
              billingCycle === "monthly" 
                ? "bg-blue-500 text-white" 
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
              billingCycle === "yearly" 
                ? "bg-blue-500 text-white" 
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            Yearly
            <Badge className="bg-green-500/20 text-green-400 text-xs">Save 20%</Badge>
          </button>
        </div>

        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-6 mb-8 text-sm text-slate-500">
          <span>✓ Card required, $0 charged today</span>
          <span>✓ Cancel anytime</span>
          <span>✓ 3D Secure enabled</span>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="container mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`bg-slate-900 border-slate-800 relative overflow-hidden transition-transform hover:scale-[1.02] ${
                plan.id === "ai_sales_agent" ? "ring-2 ring-purple-500" : ""
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.id === "ai_sales_agent" && (
                <div className="absolute top-0 right-0">
                  <Badge className="bg-purple-500 rounded-none rounded-bl-lg">
                    <Star className="w-3 h-3 mr-1" /> Most Popular
                  </Badge>
                </div>
              )}

              <CardHeader className={`bg-gradient-to-br ${getPlanColor(plan.id)} text-white`}>
                <div className="flex items-center gap-3 mb-2">
                  {getPlanIcon(plan.id)}
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                </div>
                <CardDescription className="text-white/80">
                  {plan.is_enterprise ? "Custom pricing" : (
                    <>
                      <span className="text-3xl font-bold">
                        ${billingCycle === "yearly" 
                          ? Math.round(plan.price_yearly / 12) 
                          : plan.price_monthly}
                      </span>
                      <span className="text-sm">/month</span>
                      {billingCycle === "yearly" && (
                        <p className="text-xs mt-1">Billed ${plan.price_yearly}/year</p>
                      )}
                    </>
                  )}
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6">
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400" />
                    <span>{plan.minutes_included === -1 ? "Unlimited" : plan.minutes_included.toLocaleString()} minutes/mo</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400" />
                    <span>{plan.agents === -1 ? "Unlimited" : plan.agents} AI Agents</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-400" />
                    <span>{plan.trial_days}-day free trial</span>
                  </div>
                  {plan.overage_rate > 0 && (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <span>${plan.overage_rate}/min overage</span>
                    </div>
                  )}
                  {plan.outbound_enabled && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-blue-400" />
                      <span>Outbound calling</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2 mb-6 text-sm text-slate-400">
                  {plan.features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-slate-500" />
                      <span className="capitalize">{feature.replace(/_/g, " ")}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  className={`w-full ${
                    plan.is_enterprise 
                      ? "bg-slate-700 hover:bg-slate-600" 
                      : `bg-gradient-to-r ${getPlanColor(plan.id)} hover:opacity-90`
                  }`}
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={selectedPlan === plan.id}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {plan.is_enterprise ? "Contact Sales" : "Start Free Trial"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ / Trust Section */}
        <div className="mt-16 text-center">
          <p className="text-slate-400 mb-4">
            Questions? Call us at <a href="tel:+12893356542" className="text-blue-400 hover:underline">+1 (289) 335-6542</a> or 
            email <a href="mailto:info@gskproductions.ca" className="text-blue-400 hover:underline">info@gskproductions.ca</a>
          </p>
          <p className="text-sm text-slate-500">
            Canadian-owned. Enterprise-grade security. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
